/**
 * LIRE UN DOCUMENT DE CAPACITÉS OGC — l'inverse exact de `domaine/ogc.ts`.
 *
 * ⚠️⚠️ POURQUOI UN LECTEUR, ALORS QUE `/ogc` EST DESTINÉ À QGIS. Précisément
 * pour ça. L'écran d'administration du service ne doit pas interroger une
 * route JSON taillée pour lui : il lit **le document que reçoit le tiers**.
 * Une route de confort divergerait du vrai document au premier changement de
 * catalogue, et l'écran annoncerait alors des couches que QGIS ne verrait pas
 * — ou l'inverse, ce qui est pire : on croirait avoir fermé une couche qui
 * reste servie.
 *
 * ⚠️ NI DOM, NI DÉPENDANCE. `DOMParser` n'existe pas dans Node, donc un
 * lecteur qui s'en sert ne peut pas être éprouvé au banc — et c'est justement
 * ce lecteur-là qu'il faut confronter au générateur. Le balayage est explicite
 * et se joue des deux côtés. Voir `tests/lecture-ogc.test.ts`, qui alimente ce
 * lecteur avec la sortie RÉELLE de `capacitesWfs` / `capacitesWms`.
 *
 * ⚠️ CE N'EST PAS UN ANALYSEUR XML GÉNÉRAL, et il ne prétend pas l'être. Il lit
 * UN document, celui que nous produisons, dont la forme est fixée par
 * `domaine/ogc.ts`. Le banc le prouve à chaque exécution ; le jour où le
 * générateur change, il rougit — ce qui est exactement le service attendu.
 */

export interface CoucheAnnoncee {
  /** Le nom demandable, sans préfixe d'espace de noms (`cad:parcelle` → `parcelle`). */
  nom: string;
  titre: string;
  resume: string;
}

/** Les cinq entités que `echapperXml` produit, et elles seules. */
function desechapper(v: string): string {
  return v
    .replace(/&lt;/g, '<').replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"').replace(/&apos;/g, "'")
    /* ⚠️ `&amp;` EN DERNIER, ET CE N'EST PAS UN DÉTAIL. En premier, « &amp;lt; »
       deviendrait « &lt; » puis « < » : un texte qui contenait littéralement
       « &lt; » se transformerait en balise. L'échappement s'annule dans
       l'ordre inverse de sa pose. */
    .replace(/&amp;/g, '&');
}

/** Le contenu de la PREMIÈRE balise `<nom>` du fragment, ou `null`. */
function balise(fragment: string, nom: string): string | null {
  const ouvre = `<${nom}>`;
  const ferme = `</${nom}>`;
  const i = fragment.indexOf(ouvre);
  if (i < 0) return null;
  const j = fragment.indexOf(ferme, i + ouvre.length);
  if (j < 0) return null;
  return desechapper(fragment.slice(i + ouvre.length, j)).trim();
}

/**
 * Découpe le document en fragments ouverts par `<balise`, chacun arrêté à sa
 * propre fermeture.
 *
 * ⚠️⚠️ C'EST CE DÉCOUPAGE QUI REND LE WMS LISIBLE. Ses couches sont IMBRIQUÉES :
 * un `<Layer>` racine, sans `<Name>`, enveloppe les couches demandables. Un
 * balayage naïf « du premier `<Layer` au premier `</Layer>` » attraperait le
 * conteneur et rendrait UNE couche qui n'existe pas. Ici, le fragment du
 * conteneur s'arrête à l'ouverture de son premier enfant : il ne contient
 * aucune fermeture, aucun nom, et il est écarté deux fois plutôt qu'une.
 */
function fragments(xml: string, baliseOuvrante: string): string[] {
  const morceaux = xml.split(`<${baliseOuvrante}`);
  morceaux.shift(); // l'en-tête, avant la première ouverture
  return morceaux.map((m) => {
    const fin = m.indexOf(`</${baliseOuvrante}>`);
    return fin < 0 ? m : m.slice(0, fin);
  });
}

function lire(xml: string, baliseOuvrante: string): CoucheAnnoncee[] {
  const sorties: CoucheAnnoncee[] = [];
  for (const f of fragments(xml, baliseOuvrante)) {
    const nom = balise(f, 'Name');
    /* ⚠️ SANS `<Name>`, PAS DE COUCHE — et c'est la règle OGC elle-même, pas
       une commodité : une couche sans nom n'est pas demandable, elle ne sert
       qu'à grouper. L'afficher ferait promettre une couche que le tiers ne
       pourra jamais tirer. */
    if (!nom) continue;
    sorties.push({
      // `cad:parcelle` → `parcelle`. Le préfixe est un espace de noms, pas le nom.
      nom: nom.includes(':') ? nom.slice(nom.indexOf(':') + 1) : nom,
      titre: balise(f, 'Title') ?? nom,
      resume: balise(f, 'Abstract') ?? '',
    });
  }
  return sorties;
}

/** Les couches que le WFS annonce à CET appelant. */
export function lireCouchesWfs(xml: string): CoucheAnnoncee[] {
  return lire(xml, 'FeatureType');
}

/**
 * Les couches que le WMS annonce à CET appelant.
 *
 * ⚠️ Le `<Layer>` racine porte un `<Title>` et AUCUN `<Name>` : il est écarté.
 */
export function lireCouchesWms(xml: string): CoucheAnnoncee[] {
  return lire(xml, 'Layer');
}

/**
 * Ce qu'un refus OGC dit vraiment.
 *
 * ⚠️ UN SERVICE OGC NE REND PAS UN CODE HTTP UTILE : il rend 200 avec un
 * `ServiceExceptionReport`, ou un code d'erreur enveloppé dans du XML. Lire le
 * code HTTP seul ferait présenter un refus d'habilitation comme un succès.
 */
export function lireExceptionOgc(xml: string): { code: string; message: string } | null {
  /**
   * ⚠️⚠️ `indexOf('<ServiceException')` ATTRAPE L'ENVELOPPE. Le document
   * s'ouvre sur `<ServiceExceptionReport version="1.3.0" …>` — un préfixe
   * exact de la balise cherchée. Mon premier jet lisait donc les attributs du
   * RAPPORT, n'y trouvait pas de `code`, et rendait « inconnu » sur une
   * exception parfaitement nommée : un refus d'habilitation se serait présenté
   * comme une erreur non identifiée. Trouvé par le banc, pas par relecture.
   * Le discriminant : la balise se termine par une espace ou par `>`.
   */
  const i = ['<ServiceException ', '<ServiceException>']
    .map((t) => xml.indexOf(t))
    .filter((k) => k >= 0)
    .sort((a, b) => a - b)[0] ?? -1;
  if (i < 0) return null;
  const finOuvrante = xml.indexOf('>', i);
  const fin = xml.indexOf('</ServiceException>', finOuvrante);
  if (finOuvrante < 0 || fin < 0) return null;
  const attributs = xml.slice(i, finOuvrante);
  const m = attributs.match(/code="([^"]*)"/);
  return {
    code: m ? desechapper(m[1]!) : 'inconnu',
    message: desechapper(xml.slice(finOuvrante + 1, fin)).trim(),
  };
}
